import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { accessLevelsApi } from '../accessLevelsApi';
import type { AccessLevel } from '../accessLevelTypes';
import { sampleAccessLevel } from '../utils/sampleAccessLevel';

/**
 * Create or edit one rung on the clearance ladder.
 *
 * **The rank is collected, unlike every sort order in this product.** A position in a list is
 * the server's to assign; a rank is the MEANING of the row — Level 1 is the strongest clearance
 * because its rank is 1 — so an administrator placing a rung between two others is making a
 * statement rather than asking to see it drawn somewhere.
 *
 * The edit says out loud what moving a rung does, because the consequence is invisible on this
 * form: weakening Level 1 to rank 3 makes every file classified there readable by every reader
 * ranked 3, and nobody is told.
 */
export function AccessLevelModal({
  open,
  editing,
  takenRanks,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: AccessLevel | null;
  /** The ranks already in use, so Autogenerate fills a rank the server will accept. */
  takenRanks: readonly number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [rank, setRank] = useState(editing ? String(editing.rank) : '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const rankValue = Number(rank);
  const rankIsValid = rank.trim().length > 0 && Number.isInteger(rankValue) && rankValue >= 1;

  const valid = name.trim().length > 0 && rankIsValid;
  const canSave = valid && confirmed && !saving;

  // The rung being edited keeps its own rank, so the form does not report a clash with itself.
  const clashes = rankIsValid && rankValue !== editing?.rank && takenRanks.includes(rankValue);

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      rank: rankValue,
      description: description.trim() || null,
    };

    try {
      if (editing) await accessLevelsApi.update(editing.id, request);
      else await accessLevelsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this access level.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'access-level' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleAccessLevel(takenRanks);

    setName(sample.name);
    setRank(String(sample.rank));
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit access level' : 'New access level'}
      size="lg"
      id="access-level-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'access-level-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="access-level-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="access-level-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="access-level-name"
          label="Name"
          required
          description="What the reader picks when classifying a record — Level 1, Level 2."
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
          disabled={saving}
        />

        <TextField
          id="access-level-rank"
          label="Rank"
          required
          type="number"
          min={1}
          step={1}
          description="1 is the strongest clearance. The number rises as the clearance weakens, so a reader ranked 2 reads rank 2 and below and never rank 1."
          value={rank}
          onChange={(e) => setRank(e.target.value)}
          error={
            !rankIsValid
              ? 'Enter a whole number from 1 up.'
              : clashes
                ? 'Another access level already holds that rank.'
                : error?.fieldError('rank')
          }
          disabled={saving}
        />

        <TextField
          id="access-level-description"
          label="Description"
          description="One line saying what this rung means. Shown on this list, not beside the picker."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        {/*
          Said on the form rather than on the tick, because the consequence is not visible in
          anything above: this is the one edit in the product that can hand a reader records
          they could not see a moment ago, or take records away from a reader who could.
        */}
        <p className="text-xs text-slate-500">
          {editing
            ? 'Changing a rank moves every record classified at this level and every account tagged with it. Weakening a rung makes its records readable by more people; strengthening one takes them out of sight.'
            : 'A new rung becomes a choice wherever a record is classified, and a rung an account can be tagged with. It classifies nothing on its own.'}
        </p>
      </div>
    </Modal>
  );
}
